import { apiGetRequest } from "helpers/Requests";
import {
  URL_ROSTERING_GET_NDIS_LINE_ITEMS_ON_SCHEDULING_SHIFT,
  URL_ROSTERING_INVOICING_GET_ITEM_NUMBER_ON_TIMESHEET,
} from "store/services/URL";

const resolvedCache = new Map();
const inflightCache = new Map();

const getCachedOrFetch = (cacheKey, fetcher) => {
  if (resolvedCache.has(cacheKey)) {
    return Promise.resolve(resolvedCache.get(cacheKey));
  }
  if (inflightCache.has(cacheKey)) {
    return inflightCache.get(cacheKey);
  }

  const promise = fetcher()
    .then((data) => {
      resolvedCache.set(cacheKey, data);
      inflightCache.delete(cacheKey);
      return data;
    })
    .catch((error) => {
      inflightCache.delete(cacheKey);
      throw error;
    });

  inflightCache.set(cacheKey, promise);
  return promise;
};

export const clearNdisLineItemsCache = () => {
  resolvedCache.clear();
  inflightCache.clear();
};

export const invalidateShiftNdisLineItemsCache = ({
  serviceTypeId,
  participantId,
  shiftDate,
  sleepover = false,
  sta = false,
}) => {
  const cacheKey = buildShiftNdisItemsCacheKey({
    serviceTypeId,
    participantId,
    shiftDate,
    sleepover,
    sta,
  });
  resolvedCache.delete(cacheKey);
  inflightCache.delete(cacheKey);
};

export const invalidateTimesheetNdisLineItemsCache = (params) => {
  const cacheKey = buildTimesheetNdisItemsCacheKey(params);
  resolvedCache.delete(cacheKey);
  inflightCache.delete(cacheKey);
};

const normalizeBooleanQueryValue = (value) =>
  value === true || value === "true" || value === 1 ? "true" : "false";

export const buildShiftNdisItemsCacheKey = ({
  serviceTypeId,
  participantId,
  shiftDate,
  sleepover = false,
  sta = false,
}) => {
  const sleepoverFlag = normalizeBooleanQueryValue(sleepover);
  const staFlag = normalizeBooleanQueryValue(sta);
  return `shift_ndis:${serviceTypeId}:${participantId}:${shiftDate}:${sleepoverFlag}:${staFlag}`;
};

export const buildTimesheetNdisItemsCacheKey = ({
  serviceTypeId,
  stateId,
  uuid,
  sleepover,
  categoryName,
  startTime,
  endTime,
}) =>
  `ts_ndis:${serviceTypeId}:${stateId}:${uuid}:${sleepover}:${categoryName || ""}:${startTime}:${endTime}`;

/**
 * Cached shift_ndis_items fetch (dedupes in-flight + identical params).
 */
export const fetchShiftNdisLineItems = ({
  serviceTypeId,
  participantId,
  shiftDate,
  sleepover = false,
  sta = false,
}) => {
  const cacheKey = buildShiftNdisItemsCacheKey({
    serviceTypeId,
    participantId,
    shiftDate,
    sleepover,
    sta,
  });

  const sleepoverQuery = normalizeBooleanQueryValue(sleepover);
  const staQuery = normalizeBooleanQueryValue(sta);

  return getCachedOrFetch(cacheKey, async () => {
    const res = await apiGetRequest(
      `${URL_ROSTERING_GET_NDIS_LINE_ITEMS_ON_SCHEDULING_SHIFT}?invoice_service_type_id=${serviceTypeId}&pt_id=${participantId}&shift_date=${encodeURIComponent(shiftDate)}&sleepover=${sleepoverQuery}&sta=${staQuery}`
    );
    if (res.status !== 200) {
      throw new Error("Failed to load NDIS line items");
    }
    return res?.data?.data?.items ?? [];
  });
};

/**
 * Cached timesheet-scoped NDIS items fetch.
 */
export const fetchTimesheetNdisLineItems = ({
  serviceTypeId,
  stateId,
  uuid,
  sleepover,
  categoryName,
  startTime,
  endTime,
}) => {
  const cacheKey = buildTimesheetNdisItemsCacheKey({
    serviceTypeId,
    stateId,
    uuid,
    sleepover,
    categoryName,
    startTime,
    endTime,
  });

  const categoryQuery = categoryName
    ? `&invoice_category_name=${encodeURIComponent(categoryName)}`
    : "";

  return getCachedOrFetch(cacheKey, async () => {
    const res = await apiGetRequest(
      `${URL_ROSTERING_INVOICING_GET_ITEM_NUMBER_ON_TIMESHEET}/?invoice_service_type_id=${serviceTypeId}&state_id=${stateId}&uuid=${uuid}&sleepover=${sleepover}${categoryQuery}&start_time=${startTime}&end_time=${endTime}`
    );
    if (res.status !== 200) {
      throw new Error("Failed to load NDIS line items");
    }
    return res?.data?.data?.items ?? [];
  });
};
